import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { ConversationEntityType } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { visitIQAiInsightsSidebarWidth } from '~/constants/localStorage';
import { ConversationSection } from '~/types/api.graphql';

const DEFAULT_SIDEBAR_WIDTH = 400;
const MIN_SIDEBAR_WIDTH = 280;

const computeMaxSidebarWidth = (appDrawerOpen: boolean) => Math.floor(window.innerWidth * (appDrawerOpen ? 0.4 : 0.5));

const readStoredWidth = (maxWidth: number): number => {
  const stored = localStorage.getItem(visitIQAiInsightsSidebarWidth);
  if (!stored) return Math.min(DEFAULT_SIDEBAR_WIDTH, maxWidth);
  const parsed = parseInt(stored, 10);
  return isNaN(parsed)
    ? Math.min(DEFAULT_SIDEBAR_WIDTH, maxWidth)
    : Math.max(MIN_SIDEBAR_WIDTH, Math.min(maxWidth, parsed));
};

export { DEFAULT_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH };

interface SidebarOpenParams {
  section?: ConversationSection;
  entityId?: string;
  entityType?: ConversationEntityType;
  entityDeepLink?: string;
}

interface AIInsightsSidebarContextValue {
  isOpen: boolean;
  section: ConversationSection | null;
  entityId: string | null;
  entityType: ConversationEntityType | null;
  entityDeepLink: string | null;
  sidebarWidth: number;
  maxSidebarWidth: number;
  isResizing: boolean;
  conversationId: string | null;
  clickCounterCTA: number;
  setSidebarWidth: (width: number) => void;
  setIsResizing: (resizing: boolean) => void;
  setConversationId: (id: string | null) => void;
  setIsAppDrawerOpen: (open: boolean) => void;
  openSidebar: (params: SidebarOpenParams) => void;
  closeSidebar: () => void;
  updateEntityDeepLink: (deepLink: string) => void;
}

const AIInsightsSidebarContext = createContext<AIInsightsSidebarContextValue | null>(null);

export const AIInsightsSidebarProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [section, setSection] = useState<ConversationSection | null>(null);
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<ConversationEntityType | null>(null);
  const [entityDeepLink, setEntityDeepLink] = useState<string | null>(null);
  const [isAppDrawerOpen, setIsAppDrawerOpen] = useState(false);
  const [maxSidebarWidth, setMaxSidebarWidth] = useState(() => computeMaxSidebarWidth(false));
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => readStoredWidth(computeMaxSidebarWidth(false)));
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    const update = () => {
      const newMax = computeMaxSidebarWidth(isAppDrawerOpen);
      setMaxSidebarWidth(newMax);
      setSidebarWidth((prev) => Math.min(prev, newMax));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isAppDrawerOpen]);

  useEffect(() => {
    document.body.classList.toggle('ai-sidebar-open', isOpen);
    return () => document.body.classList.remove('ai-sidebar-open');
  }, [isOpen]);

  const [conversationId, setConversationId] = useState<string | null>(null);
  // to know that a CTA for same section is clicked
  const [clickCounterCTA, setClickCounterCTA] = useState(0);

  const openSidebar = useCallback(
    ({ section = null, entityId = null, entityType = null, entityDeepLink }: SidebarOpenParams) => {
      setSection(section);
      setEntityId(entityId);
      setEntityType(entityType);
      setEntityDeepLink(entityDeepLink ?? null);
      if (!isOpen) {
        setConversationId(null);
      }
      setIsOpen(true);
      setClickCounterCTA((value) => value + 1);
    },
    [isOpen],
  );

  const closeSidebar = useCallback(() => {
    setConversationId(null);
    setIsOpen(false);
  }, []);

  const updateEntityDeepLink = useCallback((deepLink: string) => {
    setEntityDeepLink(deepLink);
  }, []);

  return (
    <AIInsightsSidebarContext.Provider
      value={{
        isOpen,
        section,
        entityId,
        entityType,
        entityDeepLink,
        sidebarWidth,
        maxSidebarWidth,
        isResizing,
        conversationId,
        clickCounterCTA,
        setSidebarWidth,
        setIsResizing,
        setConversationId,
        setIsAppDrawerOpen,
        openSidebar,
        closeSidebar,
        updateEntityDeepLink,
      }}
    >
      {children}
    </AIInsightsSidebarContext.Provider>
  );
};

export const useAIInsightsSidebar = () => useContext(AIInsightsSidebarContext);
